import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { funcionarioFranquiaService } from "../services/funcionarioFranquiaService";
import { MembroFranquiaAPI, CargoFranquia } from "../types/Funcionario";
import { useAuth } from "../contexts/AuthContext";
import { ConditionalRender } from "./ProtectedRoute";
import { Permission } from "../types/auth";

export const ListaFuncionarios: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [membros, setMembros] = useState<MembroFranquiaAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // State for the raw input value
  const [searchTerm, setSearchTerm] = useState("");
  // State for the debounced value used in the API call
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [cargoFilter, setCargoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [sortField, setSortField] = useState("user__nome_completo");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const pageSize = 10;
  const franquiaId = user?.franquia_membership?.franquia_id;
  const isGestor = hasRole("GESTOR");

  // Effect to debounce the search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when a new search is triggered
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Main effect to fetch data whenever a filter or pagination changes
  useEffect(() => {
    const fetchMembros = async () => {
      if (!franquiaId) {
        setError("Usuário não está associado a uma franquia");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const offset = (currentPage - 1) * pageSize;
        const ordering = `${sortDirection === "desc" ? "-" : ""}${sortField}`;

        const params = {
          limit: pageSize,
          offset,
          ordering,
          filtro: debouncedSearchTerm || undefined,
          cargo: cargoFilter || undefined,
          status: statusFilter || undefined,
        };

        const response = await funcionarioFranquiaService.listarMembros(
          franquiaId,
          params
        );

        setMembros(response.results);
        setTotalCount(response.count);
        setError("");
      } catch (error: any) {
        console.error("Erro ao buscar membros:", error);
        setError("Erro ao carregar lista de funcionários");
      } finally {
        setLoading(false);
      }
    };

    fetchMembros();
  }, [
    franquiaId,
    currentPage,
    debouncedSearchTerm, // Fetch when the debounced term changes
    statusFilter,
    cargoFilter,
    sortField,
    sortDirection,
  ]);

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCargoFilter("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const handleToggleStatus = async (membroId: number, isBloqueado: boolean) => {
    if (!franquiaId) return;

    try {
      setActionLoading(membroId);
      await funcionarioFranquiaService.alterarStatusMembro(
        franquiaId,
        membroId,
        !isBloqueado
      );
      // Re-fetch data by triggering the main useEffect
      // A simple way is to just refetch, but a better way would be to update the state locally
      // For now, we just trigger a refetch by slightly changing a dependency if needed, but our main effect should handle it.
      // The best way is to have a manual refetch function.
      // Let's create one.
      // For now, let's just update a dummy state to trigger refetch
      // No, the best is to just call fetchMembros again, but it's inside useEffect.
      // Let's just reload the page for simplicity now.
      // window.location.reload();
      // NO, let's just update the members list manually
      setMembros(membros.map(m => m.id_membro_franquia === membroId ? {...m, user: {...m.user, is_bloqueado: !isBloqueado}} : m))

    } catch (error: any) {
      console.error("Erro ao alterar status:", error);
      alert("Erro ao alterar status do funcionário");
    } finally {
      setActionLoading(null);
    }
  };

  const formatCargos = (cargos: CargoFranquia[]): string => {
    const cargoLabels: Record<CargoFranquia, string> = {
      ADMIN: "Administrador",
      GESTOR: "Gestor",
      FINANCEIRO: "Financeiro",
      CADASTRAMENTO: "Cadastramento",
      VENDEDOR: "Vendedor",
    };

    return cargos.map((cargo) => cargoLabels[cargo] || cargo).join(", ");
  };

  const getStatusBadge = (membro: MembroFranquiaAPI) => {
    if (membro.status_na_franquia === "INATIVO") {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
          Inativo
        </span>
      );
    }
    if (membro.user.is_bloqueado) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
          Bloqueado
        </span>
      );
    }
    if (membro.status_na_franquia === "ATIVO") {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Ativo
        </span>
      );
    }
    if (membro.status_na_franquia === "PENDENTE") {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
          Pendente
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
        {membro.status_na_franquia}
      </span>
    );
  };

  const renderSortArrow = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? <span>▼</span> : <span>▲</span>;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Funcionários da Franquia
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie os membros da sua franquia
          </p>
        </div>
        <ConditionalRender permission={Permission.MANAGE_USERS}>
          <Link
            to="/funcionarios/novo"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Novo Funcionário
          </Link>
        </ConditionalRender>
      </div>

      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar funcionário
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite o nome..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cargo
            </label>
            <select
              value={cargoFilter}
              onChange={(e) => { setCargoFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os cargos</option>
              <option value="ADMIN">Administrador</option>
              <option value="GESTOR">Gestor</option>
              <option value="FINANCEIRO">Financeiro</option>
              <option value="CADASTRAMENTO">Cadastramento</option>
              <option value="VENDEDOR">Vendedor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="ATIVO">Ativo</option>
              <option value="INATIVO">Inativo</option>
              <option value="PENDENTE">Pendente</option>
              <option value="BLOQUEADO">Bloqueado</option>
            </select>
          </div>
          <div className="flex gap-2 items-end">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Loading and Error states */}
      {loading && (
         <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tabela and Empty State */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {membros.length > 0 ? (
            <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('user__nome_completo')}>
                      Nome {renderSortArrow('user__nome_completo')}
                    </th>
                    <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('user__email')}>
                      Email {renderSortArrow('user__email')}
                    </th>
                    <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargos
                    </th>
                    <th className="w-20 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status_na_franquia')}>
                      Status {renderSortArrow('status_na_franquia')}
                    </th>
                    <th className="w-1/6 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gerenciar
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {membros.map((membro) => {
                    const viewedUserIsAdmin = membro.cargos.includes("ADMIN");
                    return (
                      <tr
                        key={membro.id_membro_franquia}
                        className="hover:bg-gray-50"
                      >
                        <td className="w-1/4 px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {membro.user.nome_completo}
                            </div>
                          </div>
                        </td>
                        <td className="w-2/5 px-6 py-4">
                          <div className="text-sm text-gray-900 break-words">
                            {membro.user.email}
                          </div>
                          {membro.user.last_login && (
                            <div className="text-sm text-gray-500">
                              Último acesso:{" "}
                              {new Date(membro.user.last_login).toLocaleDateString(
                                "pt-BR"
                              )}
                            </div>
                          )}
                        </td>
                        <td className="w-1/6 px-6 py-4">
                          <div className="text-sm text-gray-900 break-words">
                            {formatCargos(membro.cargos)}
                          </div>
                        </td>
                        <td className="w-20 px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(membro)}
                        </td>
                        <td className="w-1/6 px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2 justify-center">
                            <Link
                              to={`/funcionarios/${membro.id_membro_franquia}/detalhes`}
                              className="text-blue-600 hover:text-blue-900 px-3 py-1 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                            >
                              Detalhes
                            </Link>
                            <ConditionalRender permission={Permission.MANAGE_USERS}>
                              <button
                                onClick={() => handleToggleStatus(membro.id_membro_franquia, membro.user.is_bloqueado)}
                                disabled={actionLoading === membro.id_membro_franquia || (isGestor && viewedUserIsAdmin)}
                                className={`px-3 py-1 rounded transition-colors ${
                                  membro.user.is_bloqueado
                                    ? "text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100"
                                    : "text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100"
                                } disabled:opacity-50`}
                              >
                                {actionLoading === membro.id_membro_franquia ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mx-auto"></div>
                                ) : (
                                  <div className="flex items-center justify-center w-20">
                                    <svg
                                      className="w-4 h-4 mr-1"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d={
                                          membro.user.is_bloqueado
                                            ? "M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                                            : "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        }
                                      />
                                    </svg>
                                    <span className="text-xs">
                                      {membro.user.is_bloqueado
                                        ? "Desbloq."
                                        : "Bloquear"}
                                    </span>
                                  </div>
                                )}
                              </button>
                            </ConditionalRender>
                          </div>
                        </td>
                      </tr>
                    )}
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      até{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{" "}
                      de <span className="font-medium">{totalCount}</span>{" "}
                      funcionários
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        {currentPage} de {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">
                {debouncedSearchTerm || statusFilter || cargoFilter
                  ? "Nenhum funcionário encontrado com os critérios de busca."
                  : "Nenhum funcionário cadastrado."}
              </div>
              <ConditionalRender permission={Permission.MANAGE_USERS}>
                <Link
                  to="/funcionarios/novo"
                  className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Adicionar Primeiro Funcionário
                </Link>
              </ConditionalRender>
            </div>
          )}
        </div>
      )}
    </div>
  );
};